import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { FlagsContext } from 'src/context/flagsContext';
import { isElectron } from 'src/hooks/useElectron';
import { useAppSelector } from 'src/hooks/useStore';
import { selectCurrentUser } from 'src/store/storeUtils';
import { TrackEvent } from 'src/utils/AnalyticsUtils';

/** Maps current hostname to the assembly:// protocol scheme for deep links. */
function getAssemblyProtocol(): string {
  const { hostname } = new URL(window.location.origin);
  if (hostname.endsWith('.assembly-staging.com')) return 'assembly-staging';
  if (hostname === 'localhost') return 'assembly-dev';
  return 'assembly';
}

/**
 * Maps webapp hostname → desktop detection server port.
 * Uses suffix matching to stay consistent with getEnvironmentFromHostname.
 * Must stay in sync with DETECTION_PORTS in desktop/src/config/urls.ts.
 */
export function getDetectionPort(hostname: string): number {
  if (hostname.endsWith('.assembly-staging.com')) return 44551;
  if (hostname === 'localhost') return 44552;
  return 44550;
}

const DETECTION_TIMEOUT_MS = 500;
/** How long to show interstitial when app is confirmed running. */
const AUTO_DISMISS_DETECTED_MS = 30_000;
/** How long to show interstitial when app is not detected (quick fallback). */
const AUTO_DISMISS_NOT_DETECTED_MS = 5_000;

interface DesktopAppRedirectState {
  isRedirecting: boolean;
  dismiss: () => void;
  appDetected: boolean | null;
  retryDeepLink: () => void;
}

function getEnvironmentFromHostname(hostname: string): string {
  if (hostname.endsWith('.assembly-staging.com')) return 'staging';
  if (hostname === 'localhost') return 'development';
  return 'production';
}

function buildDeepLinkUrl(): string {
  const { pathname, search, hash } = window.location;
  const path = pathname.replace(/^\/+/, '') || '/';
  return `${getAssemblyProtocol()}://${path}${search}${hash}`;
}

function createDeepLinkIframe(): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = buildDeepLinkUrl();
  document.body.appendChild(iframe);
  return iframe;
}

/**
 * Redirects to the Desktop app (if the user has enabled the setting)
 * by attempting to open a deep link to the current page and detecting
 * whether the desktop app is running via a localhost health check.
 *
 * Returns `isRedirecting` (true while the deep-link attempt is active),
 * `appDetected` (null while detecting, true/false after),
 * `retryDeepLink` to re-attempt the deep link, and
 * a `dismiss` callback the user can invoke to cancel and stay in the browser.
 */
export const useDesktopAppRedirect = (): DesktopAppRedirectState => {
  const { fields: { openDeepLinksInDesktopApp = false } = {} } =
    useAppSelector(selectCurrentUser) ?? {};
  const { EnableDesktopAppAutolaunch } = useContext(FlagsContext);

  const shouldRedirect =
    !!EnableDesktopAppAutolaunch &&
    !!openDeepLinksInDesktopApp &&
    !isElectron();

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [appDetected, setAppDetected] = useState<boolean | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsRedirecting(false);
    if (iframeRef.current) {
      iframeRef.current.remove();
      iframeRef.current = null;
    }
  }, []);

  const retryDeepLink = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (iframeRef.current) {
      iframeRef.current.remove();
      iframeRef.current = null;
    }
    // Bump retryCount to re-trigger Effect 1 (detection + deep link).
    setRetryCount((c) => c + 1);
  }, []);

  // Effect 1: Fire deep link and start detection.
  // isRedirecting is driven from here (not useState initializer) because
  // selectCurrentUser may be undefined on first mount — ElectronIntegration
  // renders before WithUserInitialization in the component tree.
  useEffect(() => {
    if (!shouldRedirect) {
      setIsRedirecting(false);
      return;
    }
    setIsRedirecting(true);
    setAppDetected(null);

    iframeRef.current = createDeepLinkIframe();

    const abortController = new AbortController();
    let unmounted = false;

    const { hostname } = window.location;
    const port = getDetectionPort(hostname);
    const startTime = performance.now();

    const timeoutId = setTimeout(
      () => abortController.abort(),
      DETECTION_TIMEOUT_MS,
    );

    // Ping the desktop app's localhost detection server.
    // targetAddressSpace opts into Chrome's Private Network Access preflight.
    fetch(`http://127.0.0.1:${port}/health`, {
      signal: abortController.signal,
      // @ts-expect-error — Chrome Private Network Access (not yet in TS lib)
      targetAddressSpace: 'loopback',
    })
      .then((res) => {
        if (unmounted) return;
        clearTimeout(timeoutId);
        const duration = performance.now() - startTime;
        const detected = res.ok;
        setAppDetected(detected);
        void TrackEvent('Desktop App Detection', {
          desktop_app_detection_result: detected ? 'detected' : 'not_detected',
          detection_duration_ms: Math.round(duration),
          environment: getEnvironmentFromHostname(hostname),
        });
      })
      .catch((err) => {
        if (unmounted) return;
        clearTimeout(timeoutId);
        setAppDetected(false);
        const duration = performance.now() - startTime;
        const result = err?.name === 'AbortError' ? 'timeout' : 'not_detected';
        void TrackEvent('Desktop App Detection', {
          desktop_app_detection_result: result,
          detection_duration_ms: Math.round(duration),
          environment: getEnvironmentFromHostname(hostname),
        });
      });

    return () => {
      unmounted = true;
      clearTimeout(timeoutId);
      abortController.abort();
      if (iframeRef.current) {
        iframeRef.current.remove();
        iframeRef.current = null;
      }
    };
  }, [shouldRedirect, retryCount]);

  // Effect 2: Auto-dismiss timer based on detection result
  useEffect(() => {
    if (!isRedirecting || appDetected === null) return;

    const delay = appDetected
      ? AUTO_DISMISS_DETECTED_MS
      : AUTO_DISMISS_NOT_DETECTED_MS;

    timerRef.current = setTimeout(() => {
      if (iframeRef.current) {
        iframeRef.current.remove();
        iframeRef.current = null;
      }
      setIsRedirecting(false);
      timerRef.current = null;
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [appDetected, isRedirecting]);

  return { isRedirecting, dismiss, appDetected, retryDeepLink };
};
